import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePropertiesList, type Property } from "../properties/api.js";
import {
  serviceCategoryLabels,
  serviceCategoryOptions,
  uploadRfqImage,
  urgencyLabels,
  urgencyOptions,
  useCreateRfq,
  type ServiceCategory,
  type Urgency,
} from "../rfqs/api.js";
import { getSkipNextSteps } from "../rfqs/submitPreference.js";
import { ChoiceList } from "../rfqs/steps/ChoiceList.js";
import { DescriptionStep } from "../rfqs/steps/DescriptionStep.js";
import { ImagesStep, useDraftImages } from "../rfqs/steps/ImagesStep.js";
import {
  INSIGHTS_SHARE_CATEGORIES,
  ShareInsightsField,
} from "../rfqs/steps/ShareInsightsField.js";
import { useBinderList } from "../properties/binder-api.js";
import { entryTypeLabels } from "../properties/binder-templates.js";

type Step = "property" | "category" | "urgency" | "description" | "images" | "binder" | "review";

const stepOrder: Step[] = [
  "property",
  "category",
  "urgency",
  "description",
  "images",
  "binder",
  "review",
];

export function NewRfq() {
  const navigate = useNavigate();
  const propertiesQuery = usePropertiesList();
  const createMutation = useCreateRfq();

  const [step, setStep] = useState<Step>("property");
  const [propertyId, setPropertyId] = useState<string>("");
  const [serviceCategory, setServiceCategory] = useState<ServiceCategory | "">("");
  const [urgency, setUrgency] = useState<Urgency | "">("");
  const [description, setDescription] = useState("");
  const { images, addFiles: addDraftFiles, removeImage } = useDraftImages();
  const [selectedBinderIds, setSelectedBinderIds] = useState<string[]>([]);
  // Property-insights sharing (opt-out, default checked). Only meaningful —
  // and only sent — for the categories the server honors it on.
  const [shareInsights, setShareInsights] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const properties = propertiesQuery.data;
  const selectedProperty = useMemo(
    () => properties?.find((p) => p.id === propertyId),
    [properties, propertyId],
  );

  // First-RFQ onboarding: if the user has no properties yet, send them
  // straight to the property form. The `?next` param tells NewProperty to
  // bounce them back here after creation instead of dropping them on the
  // Properties list.
  //
  // The `!isFetching` guard is important after returning from NewProperty:
  // the properties query was invalidated on create, so `isSuccess` is still
  // true with the stale empty array while the refetch is in flight. Without
  // this guard we'd redirect the user right back to the empty property form.
  useEffect(() => {
    if (
      propertiesQuery.isSuccess &&
      !propertiesQuery.isFetching &&
      properties &&
      properties.length === 0
    ) {
      navigate("/app/properties/new?next=/app/rfqs/new", { replace: true });
    }
  }, [propertiesQuery.isSuccess, propertiesQuery.isFetching, properties, navigate]);

  // When the user has exactly one property and hasn't picked one yet (the
  // common case right after creating their first property), skip the picker
  // and jump straight to "What kind of work?". The empty-propertyId guard
  // means tapping Back from the category step won't re-skip.
  useEffect(() => {
    if (
      propertiesQuery.isSuccess &&
      !propertiesQuery.isFetching &&
      properties &&
      properties.length === 1 &&
      propertyId === "" &&
      step === "property"
    ) {
      setPropertyId(properties[0]!.id);
      setStep("category");
    }
  }, [propertiesQuery.isSuccess, propertiesQuery.isFetching, properties, propertyId, step]);

  const stepIndex = stepOrder.indexOf(step);
  const goNext = () => setStep(stepOrder[stepIndex + 1] ?? "review");
  const goBack = () => {
    if (stepIndex === 0) {
      navigate("/app/rfqs");
      return;
    }
    setStep(stepOrder[stepIndex - 1] ?? "property");
  };

  async function submit() {
    if (!propertyId || !serviceCategory || !urgency || description.trim().length === 0) {
      setError("Please complete every step before submitting.");
      return;
    }
    setError(null);
    try {
      const rfq = await createMutation.mutateAsync({
        propertyId,
        serviceCategory,
        urgency,
        description: description.trim(),
        // Tells the server to defer the RFQ_SUBMITTED ops email until these
        // photos have been attached, so the email contains them. The server
        // notifies after this many successful attaches.
        expectedImageCount: images.length,
        // Shared Property File entries are snapshotted onto the RFQ server-side.
        ...(selectedBinderIds.length > 0 ? { binderEntryIds: selectedBinderIds } : {}),
        // Property-insights sharing is only honored (and only asked about)
        // for scope-relevant categories — omit the field for the rest.
        ...(INSIGHTS_SHARE_CATEGORIES.has(serviceCategory)
          ? { sharePropertyInsights: shareInsights }
          : {}),
      });
      // Upload photos sequentially. Mobile uplinks don't love parallel PUTs,
      // and sequential gives us a clean per-file progress UI.
      if (images.length > 0) {
        setUploadProgress({ current: 0, total: images.length });
        for (let i = 0; i < images.length; i++) {
          const img = images[i]!;
          setUploadProgress({ current: i + 1, total: images.length });
          try {
            await uploadRfqImage({
              rfqId: rfq.id,
              file: img.file,
              contentType: img.contentType,
            });
          } catch (err) {
            // Don't block navigation: the RFQ was created. Surface the
            // failure in the detail view instead.
            console.error("Image upload failed", img.file.name, err);
          }
        }
      }
      // Show the friendly "what happens next" interstitial unless the user
      // has opted out on this device. Use replace so the back button doesn't
      // return into the now-empty wizard.
      const target = getSkipNextSteps() ? `/app/rfqs/${rfq.id}` : `/app/rfqs/${rfq.id}/submitted`;
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't submit request");
    } finally {
      setUploadProgress(null);
    }
  }

  function onAddFiles(fileList: FileList | null) {
    const errors = addDraftFiles(fileList);
    setError(errors.length > 0 ? errors.join("; ") : null);
  }

  return (
    <div>
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <button type="button" onClick={goBack} className="text-sm text-teal-700">
          ‹ {stepIndex === 0 ? "Requests" : "Back"}
        </button>
        <h1 className="mt-1 text-xl font-semibold">New request</h1>
        <ol className="mt-2 flex gap-1" aria-label="Progress">
          {stepOrder.map((s, i) => (
            <li
              key={s}
              className={`h-1 flex-1 rounded-full ${
                i <= stepIndex ? "bg-teal-700" : "bg-slate-200"
              }`}
            />
          ))}
        </ol>
      </header>

      <div className="space-y-4 p-4">
        {error && <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">{error}</div>}

        {step === "property" && (
          <PropertyStep
            properties={properties}
            isLoading={propertiesQuery.isLoading}
            selectedId={propertyId}
            onSelect={(id) => {
              setPropertyId(id);
              goNext();
            }}
          />
        )}

        {step === "category" && (
          <ChoiceList
            heading="What kind of work?"
            options={serviceCategoryOptions.map((c) => ({
              value: c,
              label: serviceCategoryLabels[c],
            }))}
            selected={serviceCategory}
            onSelect={(value) => {
              setServiceCategory(value as ServiceCategory);
              goNext();
            }}
          />
        )}

        {step === "urgency" && (
          <ChoiceList
            heading="How soon do you need it done?"
            options={urgencyOptions.map((u) => ({ value: u, label: urgencyLabels[u] }))}
            selected={urgency}
            onSelect={(value) => {
              setUrgency(value as Urgency);
              goNext();
            }}
          />
        )}

        {step === "description" && (
          <DescriptionStep value={description} onChange={setDescription} onContinue={goNext} />
        )}

        {step === "images" && (
          <ImagesStep
            images={images}
            onAdd={onAddFiles}
            onRemove={removeImage}
            onContinue={goNext}
          />
        )}

        {step === "binder" && (
          <BinderAttachStep
            propertyId={propertyId}
            selected={selectedBinderIds}
            onToggle={(id) =>
              setSelectedBinderIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
              )
            }
            onContinue={goNext}
          />
        )}

        {step === "review" && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold">Review &amp; submit</h2>
            <ReviewRow label="Property">
              {selectedProperty ? (
                <>
                  <div>{selectedProperty.nickname}</div>
                  <div className="text-sm text-slate-500">
                    {selectedProperty.street}
                    {selectedProperty.unit ? `, ${selectedProperty.unit}` : ""} ·{" "}
                    {selectedProperty.city}, {selectedProperty.state}
                  </div>
                </>
              ) : (
                "—"
              )}
            </ReviewRow>
            <ReviewRow label="Type">
              {serviceCategory ? serviceCategoryLabels[serviceCategory] : "—"}
            </ReviewRow>
            <ReviewRow label="Urgency">{urgency ? urgencyLabels[urgency] : "—"}</ReviewRow>
            <ReviewRow label="Description">
              <p className="whitespace-pre-wrap">{description || "—"}</p>
            </ReviewRow>
            <ReviewRow label="Property file">
              {selectedBinderIds.length === 0
                ? "None attached"
                : `${selectedBinderIds.length} item${selectedBinderIds.length === 1 ? "" : "s"} attached`}
            </ReviewRow>
            <ReviewRow label="Photos">
              {images.length === 0 ? (
                "None"
              ) : (
                <div className="mt-1 grid grid-cols-4 gap-2">
                  {images.map((img) => (
                    <img
                      key={img.localId}
                      src={img.previewUrl}
                      alt=""
                      className="h-16 w-16 rounded object-cover"
                    />
                  ))}
                </div>
              )}
            </ReviewRow>

            {serviceCategory !== "" && INSIGHTS_SHARE_CATEGORIES.has(serviceCategory) && (
              <ShareInsightsField checked={shareInsights} onChange={setShareInsights} />
            )}

            <div className="flex gap-2">
              <button
                type="button"
                disabled={createMutation.isPending || uploadProgress !== null}
                onClick={goBack}
                className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700 disabled:opacity-60"
              >
                Go back
              </button>
              <button
                type="button"
                disabled={createMutation.isPending || uploadProgress !== null}
                onClick={submit}
                className="min-h-[44px] flex-1 rounded-md bg-teal-700 text-sm font-medium text-white disabled:opacity-60"
              >
                {createMutation.isPending
                  ? "Submitting…"
                  : uploadProgress
                    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}…`
                    : "Submit request"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PropertyStep({
  properties,
  isLoading,
  selectedId,
  onSelect,
}: {
  properties: Property[] | undefined;
  isLoading: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (isLoading) return <p className="text-sm text-slate-500">Loading properties…</p>;
  if (!properties || properties.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
        <p className="text-sm text-slate-600">
          You need at least one property before you can request work.
        </p>
        <Link
          to="/app/properties/new?next=/app/rfqs/new"
          className="mt-3 inline-flex min-h-[44px] items-center rounded-md bg-teal-700 px-4 text-sm font-medium text-white"
        >
          Add a property
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Which property?</h2>
      <ul className="space-y-2">
        {properties.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onSelect(p.id)}
              className={`block w-full rounded-lg border p-4 text-left active:bg-slate-50 ${
                selectedId === p.id ? "border-teal-700 bg-teal-50" : "border-slate-200 bg-white"
              }`}
            >
              <p className="font-medium text-slate-900">{p.nickname}</p>
              <p className="text-sm text-slate-500">
                {p.street}
                {p.unit ? `, ${p.unit}` : ""} · {p.city}, {p.state}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BinderAttachStep({
  propertyId,
  selected,
  onToggle,
  onContinue,
}: {
  propertyId: string;
  selected: string[];
  onToggle: (id: string) => void;
  onContinue: () => void;
}) {
  const { data: entries, isLoading } = useBinderList(propertyId);
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Attach from your property file</h2>
      <p className="text-sm text-slate-500">
        Optionally share saved details — like appliance specs or photos — with the pros you&apos;re
        matched with.
      </p>
      {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {!isLoading && (entries?.length ?? 0) === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
          Nothing saved for this property yet.
        </p>
      )}
      {entries && entries.length > 0 && (
        <ul className="space-y-2">
          {entries.map((e) => {
            const checked = selected.includes(e.id);
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => onToggle(e.id)}
                  aria-pressed={checked}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                    checked ? "border-teal-700 bg-teal-50" : "border-slate-200 bg-white"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                      checked ? "border-teal-700 bg-teal-700 text-white" : "border-slate-300"
                    }`}
                  >
                    {checked ? "✓" : ""}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-slate-900">{e.title}</span>
                    <span className="block truncate text-sm text-slate-500">
                      {[e.locationLabel, entryTypeLabels[e.entryType]].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={onContinue}
        className="min-h-[44px] w-full rounded-md bg-teal-700 text-sm font-medium text-white"
      >
        {selected.length > 0 ? `Continue with ${selected.length} attached` : "Skip"}
      </button>
    </div>
  );
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900">{children}</div>
    </div>
  );
}
